import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import type { CustomerStatus, GuestCartSort } from '@/features/users/api';
import type { CustomerFilters } from '@/features/users/CustomersTab';
import type { ReviewStatusFilter } from '@/features/users/reviews-api';
import type { ReviewFilters } from '@/features/users/ReviewsTab';
import { UsersPage, type UsersTab } from '@/features/users/UsersPage';

const sortValues: GuestCartSort[] = ['lastActivityAt', 'createdAt'];
const statusValues: CustomerStatus[] = ['ACTIVE', 'BLOCKED'];
const reviewStatusValues: ReviewStatusFilter[] = ['PENDING', 'PUBLISHED', 'REJECTED', 'all'];

/**
 * Очередь открывается ожидающими: разбирать её и значит смотреть, кто ждёт. У бэкенда
 * умолчание то же, но в адрес значение не пишется, пока его не переключили.
 */
const DEFAULT_REVIEW_STATUS: ReviewStatusFilter = 'PENDING';

/**
 * Порядок гостевых корзин по умолчанию — свежая активность: вкладку открывают ради вопроса
 * «кто набирает корзину прямо сейчас». У бэкенда умолчание другое (по созданию), поэтому
 * `sort` уходит в запрос всегда, а не только когда его переключили.
 */
const DEFAULT_SORT: GuestCartSort = 'lastActivityAt';

export interface UsersSearch {
  /** Вкладка раздела; по умолчанию — зарегистрированные покупатели. */
  tab?: UsersTab;
  sort?: GuestCartSort;
  status?: CustomerStatus;
  /** Код страны из номера телефона, ISO-3166 alpha-2. */
  country?: string;
  /** Строка поиска: последние цифры номера, начало e-mail или ФИО. */
  q?: string;
  /** Открытая карточка покупателя — в адресе, потому что в неё ведёт ссылка из заказа. */
  customer?: string;
  /** Статус отзывов на вкладке модерации; отдельно от `status` аккаунта — это разные перечисления. */
  review?: ReviewStatusFilter;
  /** Сузить очередь до одного товара. */
  product?: string;
  /**
   * Сузить очередь до одного покупателя. Не `customer`: тот открывает карточку, и один ключ
   * на два смысла означал бы, что ссылка «отзывы этого покупателя» распахивает поверх них шторку.
   */
  author?: string;
}

/**
 * Состояние раздела живёт в адресе: ссылку на «заблокированных из России» или прямо
 * на карточку покупателя пересылают коллеге, и открыться она должна тем же экраном.
 *
 * Чужие значения отбрасываются: `status` и `country` уходят в запрос как есть, а неизвестный
 * ключ бэкенд отклонит как `400 bad-request`.
 */
export const Route = createFileRoute('/_app/users')({
  validateSearch: (search: Record<string, unknown>): UsersSearch => ({
    tab: search.tab === 'guests' || search.tab === 'reviews' ? search.tab : undefined,
    sort: sortValues.find((value) => value === search.sort),
    status: statusValues.find((value) => value === search.status),
    // Регистр бэкенду не важен, но в адресе и в списке страна должна выглядеть одинаково —
    // иначе `by` из чужой ссылки не совпадёт ни с одним пунктом фильтра.
    country:
      typeof search.country === 'string' && search.country !== '' ? search.country.toUpperCase() : undefined,
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
    customer: typeof search.customer === 'string' && search.customer !== '' ? search.customer : undefined,
    review: reviewStatusValues.find((value) => value === search.review),
    product: typeof search.product === 'string' && search.product !== '' ? search.product : undefined,
    author: typeof search.author === 'string' && search.author !== '' ? search.author : undefined,
  }),
  component: UsersRoute,
});

function UsersRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Стабильная ссылка: вкладка держит на этом колбэке таймер задержки поиска, и новая функция
  // на каждый рендер сбрасывала бы отсчёт — строка уходила бы в адрес только после паузы.
  const onFiltersChange = useCallback(
    (next: Partial<CustomerFilters>) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          q: 'search' in next ? next.search || undefined : previous.q,
          status: 'status' in next ? (next.status ?? undefined) : previous.status,
          country: 'country' in next ? (next.country ?? undefined) : previous.country,
        }),
        // Правка фильтра — не шаг навигации: иначе «назад» отматывал бы по букве запроса.
        replace: true,
      });
    },
    [navigate],
  );

  // Стабильная ссылка не нужна вкладке отзывов так, как поиску покупателей, но и лишних
  // рендеров списка карточек она не стоит.
  const onReviewFiltersChange = useCallback(
    (next: Partial<ReviewFilters>) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          review:
            'status' in next
              ? next.status === DEFAULT_REVIEW_STATUS
                ? undefined
                : next.status
              : previous.review,
          product: 'productId' in next ? (next.productId ?? undefined) : previous.product,
          author: 'customerId' in next ? (next.customerId ?? undefined) : previous.author,
        }),
        // Смена отбора — не шаг навигации: «назад» должно уводить из раздела, а не перебирать
        // статусы очереди.
        replace: true,
      });
    },
    [navigate],
  );

  const onCustomerChange = useCallback(
    (id: string | null) => {
      void navigate({
        search: (previous) => ({ ...previous, customer: id ?? undefined }),
        // Открытие и закрытие карточки — шаг истории: «назад» из карточки возвращает
        // к списку, как и ожидается от бокового экрана, открытого по ссылке.
        replace: false,
      });
    },
    [navigate],
  );

  return (
    <UsersPage
      tab={search.tab ?? 'customers'}
      sort={search.sort ?? DEFAULT_SORT}
      filters={{
        status: search.status ?? null,
        country: search.country ?? null,
        search: search.q ?? '',
      }}
      reviewFilters={{
        status: search.review ?? DEFAULT_REVIEW_STATUS,
        productId: search.product ?? null,
        customerId: search.author ?? null,
      }}
      customerId={search.customer ?? null}
      onTabChange={(tab) =>
        void navigate({
          search: (previous) => ({ ...previous, tab: tab === 'customers' ? undefined : tab }),
          replace: true,
        })
      }
      onSortChange={(sort) =>
        void navigate({
          search: (previous) => ({ ...previous, sort: sort === DEFAULT_SORT ? undefined : sort }),
          // Смена порядка — не шаг навигации: «назад» должно уводить из раздела,
          // а не перебирать сортировки.
          replace: true,
        })
      }
      onFiltersChange={onFiltersChange}
      onReviewFiltersChange={onReviewFiltersChange}
      onCustomerChange={onCustomerChange}
    />
  );
}
